import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    IconButton, TextField, Tooltip, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import GetAppIcon from '@mui/icons-material/GetApp';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import PageTitle from "../../common/PageTitle";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import AwbLinkCell from "../../common/AwbLinkCell";
import { FormProvider, RHFTextField } from "../../common/hook-form";
import RHFMultipleSelectField from "../../common/hook-form/RHFMultipleSelectField";
import { apiConstants, getGeneric, patch, postGeneric } from "../../common/constants";
import { parseDateAndTime } from "../../helpers/UtilityHelper";
import withLayout from "../../hocs/Layout";
import DateRangePicker from "../DateRangePicker";
import { subDays } from "date-fns";
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, ERROR, withOpacity } from '../../styles/foundation/colors';
import { pageContainerSx } from '../../styles/patterns';
import CreateEscalationTicket from "./CreateEscalationTicket";
import BulkCreateTickets from "./BulkCreateTickets";
import ExportTicketsDialog from "./ExportTicketsDialog";
import { getCategoryConfig, getStatusConfig } from "./escalationConfigCache";
import { filterBtnSx } from "./ticketFormStyles";
import { getStatusChipSx } from "../orders/list2/cellStyles";
import { ACCENTS, CARD_SX, FieldLabel, KpiTile, StatusChip, rise } from "./emsUi";

const iconBtnSx = (color) => ({
    color, bgcolor: withOpacity(color, 0.1), width: 30, height: 30,
    '&:hover': { bgcolor: withOpacity(color, 0.2) },
});

// The hero's buttons, white-on-blue.
const heroBtnSx = {
    fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.bold, borderRadius: '10px', boxShadow: 'none', whiteSpace: 'nowrap',
};

// End of today, not "now": dateTo is sent as-is on first load, so a page-load timestamp would
// hide every ticket created after the page opened — including one just raised from this page.
const defaultDateRange = () => {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    return { startDate: subDays(new Date(), 30), endDate: end, key: 'selection' };
};
const defaultFilters = () => {
    const { startDate, endDate } = defaultDateRange();
    return { dateFrom: startDate.toISOString(), dateTo: endDate.toISOString() };
};
const EMPTY_FORM = { ticketId: '', awb: '', status: [], category: [], subcategory: [], courier: [], shipmentStatus: [] };

// Includes the default page size (30), which the grid's stock list doesn't — without it MUI
// hides the rows-per-page selector.
const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50, 100];

// Only a resolved ticket can be reopened from here, with a comment; a closed one stays closed
// (the server enforces both).
const canReopen = (row) => row?.statusCategory === 'RESOLVED';

// What each KPI tile shows in the list when clicked (see EscalationService.buildSearchCriteria);
// each matches how the tile is counted.
const DRILLS = {
    inProgress: { unresolvedOnly: true, awaitingClientOnly: false },
    awaiting: { awaitingClientOnly: true },
    resolved: { statusCategory: 'RESOLVED' },
    closed: { statusCategory: 'CLOSED' },
    avgResolution: { closedOnly: true },
    slaMissed: { resolutionSlaMissedOnly: true },
};
const DRILL_LABELS = {
    inProgress: 'In progress with Prozo',
    awaiting: 'Awaiting your inputs',
    resolved: 'Resolved',
    closed: 'Closed',
    avgResolution: 'Resolved or closed',
    slaMissed: 'Missed the resolution SLA',
};

const prettyStatus = (s) => String(s || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Help & Support — merchant panel, Escalation Desk V2. Reads/writes the in-house /api/escalation/*
 * backend (the same one the admin panel's EscalationManagementV2 uses).
 *
 * No merchant picker (the backend scopes every call to the logged-in merchant, see
 * EscalationService), and no bulk resolve/comment. A merchant can close a ticket, or reopen a
 * resolved one with a comment — the server allows nothing else from here.
 *
 * The KPI tiles are filters: a click narrows the list to what the tile counted, a second click
 * clears it. Rows can be ticked (across pages, or every ticket the filters match) and downloaded.
 */
const EscalationManagementV2Merchant = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(30);
    const [resData, setResData] = useState({ results: [], total: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [reloadTick, setReloadTick] = useState(0);
    // null, or { filters, count }: what the export dialog downloads.
    const [exportTarget, setExportTarget] = useState(null);
    const [closeConfirmRow, setCloseConfirmRow] = useState(null);
    const [closingTicket, setClosingTicket] = useState(false);
    const [reopenConfirmRow, setReopenConfirmRow] = useState(null);
    const [reopeningTicket, setReopeningTicket] = useState(false);
    const [reopenComment, setReopenComment] = useState("");
    const [filtersOpen, setFiltersOpen] = useState(true);

    const [categories, setCategories] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [shipmentStatuses, setShipmentStatuses] = useState([]);
    // Parent courier names on this merchant's tickets ("Delhivery"), never Prozo's account codes.
    const [courierParents, setCourierParents] = useState([]);
    const [dateRange, setDateRange] = useState([defaultDateRange()]);
    const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
    // The Status picks as the brand sees them (the request carries the internal names behind them).
    const [appliedStatusLabels, setAppliedStatusLabels] = useState([]);
    const [drill, setDrill] = useState(null);
    // Older closed tickets moved over from the previous support desk live behind this switch.
    const [showArchived, setShowArchived] = useState(false);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Ticked rows, kept across pages; allMatching = every ticket the filters match.
    const [selectedIds, setSelectedIds] = useState([]);
    const [allMatching, setAllMatching] = useState(false);

    const methods = useForm({ defaultValues: EMPTY_FORM });
    const { handleSubmit, reset, watch, setValue, getValues } = methods;
    const selectedCategories = watch('category');

    // With categories picked, their subcategories; with none, every subcategory (names that
    // repeat across categories listed once).
    const subcategoryOptions = useMemo(() => {
        const picked = Array.isArray(selectedCategories) ? selectedCategories : [];
        const source = picked.length ? categories.filter((c) => picked.includes(c.categoryName)) : categories;
        return [...new Set(source.flatMap((c) => (c.subCategories || []).map((s) => s.name)))]
            .sort((a, b) => a.localeCompare(b)).map((name) => ({ value: name, label: name }));
    }, [categories, selectedCategories]);

    // Drops picked subcategories that no longer belong to a picked category.
    useEffect(() => {
        const allowed = new Set(subcategoryOptions.map((o) => o.value));
        const current = getValues('subcategory') || [];
        const kept = current.filter((s) => allowed.has(s));
        if (kept.length !== current.length) setValue('subcategory', kept);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subcategoryOptions]);

    // One option per name the brand sees ("In Progress" covers several statuses).
    const statusOptions = useMemo(() => [...new Set(statusList.map((s) => s.externalLabel || s.label))]
        .map((name) => ({ value: name, label: name })), [statusList]);
    const categoryOptions = useMemo(() => [...categories].sort((a, b) => a.categoryName.localeCompare(b.categoryName))
        .map((c) => ({ value: c.categoryName, label: c.categoryName })), [categories]);
    const courierOptions = useMemo(() => courierParents.map((c) => ({ value: c, label: c })), [courierParents]);
    const shipmentStatusOptions = useMemo(() => shipmentStatuses.map((s) => ({ value: s, label: prettyStatus(s) })), [shipmentStatuses]);

    useEffect(() => {
        let cancelled = false;
        const fetchConfig = async () => {
            try {
                const [statuses, categoryConfig] = await Promise.all([getStatusConfig(), getCategoryConfig()]);
                if (cancelled) return;
                setStatusList(statuses);
                setCategories(categoryConfig);
            } catch (error) {
                if (!cancelled) enqueueSnackbar("Could not load filter options", { variant: "error" });
            }
        };
        // Quiet on failure: the Shipment Status / Courier filters just have no options.
        getGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/config/order-statuses`)
            .then((res) => { if (!cancelled) setShipmentStatuses(res?.data || []); })
            .catch(() => {});
        getGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/config/courier-parents`)
            .then((res) => { if (!cancelled) setCourierParents(res?.data || []); })
            .catch(() => {});
        fetchConfig();
        return () => { cancelled = true; };
    }, [enqueueSnackbar]);

    const archivedPatch = showArchived ? { archivedOnly: true } : {};
    const listFilters = useMemo(() => ({ ...appliedFilters, ...(drill ? DRILLS[drill] : {}), ...(showArchived ? { archivedOnly: true } : {}) }),
        [appliedFilters, drill, showArchived]);
    // The tiles count over the filters without their own drill-down, so their numbers stay put
    // while one of them is applied.
    const statsFilters = useMemo(() => ({ ...appliedFilters, ...(showArchived ? { archivedOnly: true } : {}) }), [appliedFilters, showArchived]);

    // A new list clears the ticks.
    useEffect(() => { setSelectedIds([]); setAllMatching(false); }, [listFilters]);

    // Each fetch takes a request id; only the latest one may paint, so a slow response for an
    // older page or filter set can't overwrite a newer one.
    const ticketsReqRef = useRef(0);
    const statsReqRef = useRef(0);

    const fetchTickets = useCallback(async () => {
        const reqId = ++ticketsReqRef.current;
        setIsLoading(true);
        try {
            const response = await postGeneric(apiConstants.ESCALATION_SEARCH_V2, { page, size: sizePerPage, ...listFilters });
            if (reqId !== ticketsReqRef.current) return;
            setResData(response?.data || { results: [], total: 0 });
        } catch (error) {
            if (reqId !== ticketsReqRef.current) return;
            enqueueSnackbar("Some error occurred while loading tickets", { variant: "error" });
        } finally {
            if (reqId === ticketsReqRef.current) setIsLoading(false);
        }
    }, [page, sizePerPage, listFilters, enqueueSnackbar]);

    // summaryOnly: the tiles read only stats.summary, so skip the breakdown/trend/ageing facets.
    const fetchStats = useCallback(async () => {
        const reqId = ++statsReqRef.current;
        setStatsLoading(true);
        try {
            const response = await postGeneric(`${apiConstants.ESCALATION_STATS_V2}?summaryOnly=true`, statsFilters);
            if (reqId !== statsReqRef.current) return;
            setStats(response?.data || null);
        } catch (error) {
            if (reqId !== statsReqRef.current) return;
            setStats(null);
        } finally {
            if (reqId === statsReqRef.current) setStatsLoading(false);
        }
    }, [statsFilters]);

    useEffect(() => { fetchTickets(); }, [fetchTickets, reloadTick]);
    useEffect(() => { fetchStats(); }, [fetchStats, reloadTick]);

    const filtersFrom = (values) => {
        const { startDate, endDate } = dateRange[0] || {};
        const ticketId = (values.ticketId || '').trim();
        // A ticket ID search looks across all dates: someone looking up a ticket they were told
        // about shouldn't miss it because it was raised outside the selected range.
        const useDates = !ticketId;
        const statuses = values.status?.length
            ? statusList.filter((st) => values.status.includes(st.externalLabel || st.label)).map((st) => st.label) : [];
        return {
            // Partial and case-insensitive on the server: "100023", "esc-100023" and "ESC-1000" all match,
            // and a bare number also finds a ticket imported from Freshdesk by its Freshdesk number.
            ...(ticketId ? { escalationNumber: ticketId } : {}),
            ...(values.awb?.trim() ? { awbNumber: values.awb.trim() } : {}),
            ...(values.category?.length ? { categories: values.category } : {}),
            ...(values.subcategory?.length ? { subCategories: values.subcategory } : {}),
            ...(statuses.length ? { statuses } : {}),
            ...(values.courier?.length ? { courierParents: values.courier } : {}),
            ...(values.shipmentStatus?.length ? { orderStatuses: values.shipmentStatus } : {}),
            ...(useDates && startDate ? { dateFrom: new Date(startDate).toISOString() } : {}),
            ...(useDates && endDate ? { dateTo: new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString() } : {}),
        };
    };

    const onSubmit = (values) => {
        setPage(0);
        setAppliedFilters(filtersFrom(values));
        setAppliedStatusLabels(values.status || []);
    };

    // Back to the page's default window rather than no dates: an all-history search and stats
    // aggregation is expensive, and isn't what the page opens with either.
    const onReset = () => {
        reset(EMPTY_FORM);
        setPage(0);
        setDrill(null);
        setDateRange([defaultDateRange()]);
        setAppliedFilters(defaultFilters());
        setAppliedStatusLabels([]);
    };

    // Removing one chip clears that field and re-applies the rest.
    const clearField = (field) => {
        setValue(field, EMPTY_FORM[field]);
        onSubmit(getValues());
    };

    const toggleDrill = (key) => {
        setPage(0);
        setDrill((d) => (d === key || key === null ? null : key));
    };

    // Close and reopen go to the first CLOSED / OPEN status, same convention as the admin panel.
    const changeStatus = async (row, category, done, setBusy, okText, failText, reason) => {
        const target = statusList.find((s) => s.category === category);
        if (!target) { enqueueSnackbar(`No ${category.toLowerCase()} status is configured`, { variant: "error" }); done(); return; }
        setBusy(true);
        try {
            await patch(`${apiConstants.BASE_LOCAL}${apiConstants.ESCALATION_DETAIL_V2}/${row.id}/status`, { status: target.label, ...(reason ? { reason } : {}) });
            enqueueSnackbar(okText, { variant: "success" });
            setReloadTick((t) => t + 1);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || failText, { variant: "error" });
        } finally {
            setBusy(false);
            done();
        }
    };
    const submitCloseTicket = () => closeConfirmRow && changeStatus(closeConfirmRow, 'CLOSED', () => setCloseConfirmRow(null),
        setClosingTicket, "Ticket closed", "Could not close ticket");
    const submitReopenTicket = () => reopenConfirmRow && reopenComment.trim() && changeStatus(reopenConfirmRow, 'OPEN',
        () => { setReopenConfirmRow(null); setReopenComment(""); }, setReopeningTicket, "Ticket reopened, it's back with Prozo's team",
        "Could not reopen ticket", reopenComment.trim());

    const totalTickets = resData?.total || 0;
    const summary = stats?.summary;
    const awaitingCount = summary?.awaitingClientCount || 0;
    const slaCompliancePct = summary?.slaBreachRatePct != null ? Math.round(100 - summary.slaBreachRatePct) : null;
    const avgResolutionHrs = summary?.avgResolutionMinutes != null ? summary.avgResolutionMinutes / 60 : null;

    const kpis = [
        { key: null, label: 'Total Tickets', value: summary?.totalCount ?? (summary ? totalTickets : '—'), accent: ACCENTS.navy, icon: ConfirmationNumberOutlinedIcon,
            sub: 'raised in this period', info: 'Every ticket raised in the selected date range. Click to clear a tile filter.' },
        { key: 'inProgress', label: 'In Progress', value: summary ? summary.unresolvedCount - awaitingCount : '—', accent: ACCENTS.blue, icon: PendingActionsOutlinedIcon,
            sub: 'Prozo is working on these', info: 'Open tickets Prozo is working on.' },
        { key: 'awaiting', label: 'Awaiting Your Inputs', value: summary ? awaitingCount : '—', accent: ACCENTS.amber, icon: HourglassEmptyOutlinedIcon,
            sub: awaitingCount ? 'reply to send back to Prozo' : 'nothing waiting on you',
            info: 'Tickets waiting for a reply from your team. Reply on the ticket to send it back to Prozo; with no reply in 72 hours it is closed.' },
        { key: 'resolved', label: 'Resolved', value: summary?.resolvedCount ?? '—', accent: ACCENTS.green, icon: CheckCircleOutlineIcon,
            sub: 'awaiting closure', info: 'Tickets now resolved. They close on their own 72 hours after resolving; reopen one, with a comment, if the issue is back.' },
        { key: 'closed', label: 'Closed', value: summary ? Math.max(0, (summary.resolvedOrClosedCount || 0) - (summary.resolvedCount || 0)) : '—', accent: ACCENTS.grey, icon: TaskAltOutlinedIcon,
            sub: 'finished', info: 'Tickets that are closed. Raise a new ticket if an issue comes back.' },
        { key: 'avgResolution', label: 'Avg Resolution', value: avgResolutionHrs != null ? `${avgResolutionHrs.toFixed(1)} hrs` : '—', accent: ACCENTS.teal, icon: AccessTimeOutlinedIcon,
            sub: 'creation to resolution', info: 'Average time from creation to resolution. Click to see the resolved and closed tickets.' },
        { key: 'slaMissed', label: 'SLA Compliance', value: slaCompliancePct != null ? `${slaCompliancePct}%` : '—', accent: ACCENTS.green, icon: VerifiedOutlinedIcon,
            sub: 'resolved on time', info: 'Of the resolved tickets, the share resolved by their due date. Click to see the tickets that missed it.' },
    ];

    // Chips for what's narrowing the list right now, each removable.
    const activeChips = [
        appliedFilters.escalationNumber && { key: 'ticketId', label: `Ticket: ${appliedFilters.escalationNumber}`, onDelete: () => clearField('ticketId') },
        appliedFilters.awbNumber && { key: 'awb', label: `AWB: ${appliedFilters.awbNumber}`, onDelete: () => clearField('awb') },
        appliedStatusLabels.length > 0 && { key: 'status', label: `Status: ${appliedStatusLabels.join(', ')}`, onDelete: () => clearField('status') },
        appliedFilters.categories && { key: 'category', label: `Category: ${appliedFilters.categories.join(', ')}`, onDelete: () => clearField('category') },
        appliedFilters.subCategories && { key: 'subcategory', label: `Subcategory: ${appliedFilters.subCategories.join(', ')}`, onDelete: () => clearField('subcategory') },
        appliedFilters.courierParents && { key: 'courier', label: `Courier: ${appliedFilters.courierParents.join(', ')}`, onDelete: () => clearField('courier') },
        appliedFilters.orderStatuses && { key: 'shipmentStatus', label: `Shipment: ${appliedFilters.orderStatuses.map(prettyStatus).join(', ')}`, onDelete: () => clearField('shipmentStatus') },
        drill && { key: 'drill', label: DRILL_LABELS[drill], onDelete: () => toggleDrill(null), accent: true },
    ].filter(Boolean);

    const selectionCount = allMatching ? totalTickets : selectedIds.length;
    const pageIds = (resData?.results || []).map((r) => r.id);
    const wholePageTicked = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    const clearSelection = () => { setSelectedIds([]); setAllMatching(false); };
    const downloadSelected = () => setExportTarget(allMatching
        ? { filters: listFilters, count: totalTickets, selection: true }
        : { filters: { ids: selectedIds, ...archivedPatch }, count: selectedIds.length, selection: true });

    const columns = [
        {
            field: 'escalationNumber', headerAlign: "left", headerName: 'Ticket #', flex: 0.35, minWidth: 110,
            align: 'left', renderCell: ({ row }) => (
                <Box component="button" type="button" onClick={() => navigate(`/support-v2/view?ticketId=${row.id}`)}
                    sx={{ all: 'unset', cursor: 'pointer', fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: PRIMARY_BLUE,
                        '&:hover': { textDecoration: 'underline' }, '&:focus-visible': { outline: `2px solid ${PRIMARY_BLUE}`, outlineOffset: 2, borderRadius: '4px' } }}>
                    {row?.escalationNumber}
                </Box>
            ),
        },
        {
            field: 'awbNumber', headerAlign: "left", flex: 0.4, headerName: 'AWB',
            align: 'left', renderCell: ({ row }) => {
                if (!row?.awbNumber) return <Typography sx={{ fontSize: SIZE.xs, color: SUBTLE }}>—</Typography>;
                return <AwbLinkCell awb={row.awbNumber} sx={{ fontFamily: FONT, fontSize: SIZE.xs }} />;
            },
        },
        {
            field: 'category', headerAlign: "left", flex: 0.8, headerName: 'Category',
            align: 'left', renderCell: ({ row }) => {
                if (!row?.category) return <Typography sx={{ fontSize: SIZE.xs, color: SUBTLE }}>—</Typography>;
                return (
                    <Box sx={{ py: 0.5 }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: WEIGHT.bold, color: NAVY, lineHeight: 1.3 }}>{row.category}</Typography>
                        {row.subCategory && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.4 }}>
                                <SubdirectoryArrowRightIcon sx={{ fontSize: 12, color: SUBTLE, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: SUBTLE, lineHeight: 1.2 }}>{row.subCategory}</Typography>
                            </Box>
                        )}
                    </Box>
                );
            },
        },
        {
            // The shipment's current status (refreshed from the order every 5 minutes while the ticket is open),
            // in the same chip the order pages use.
            field: 'orderStatus', headerAlign: "left", flex: 0.5, minWidth: 130, headerName: 'Shipment Status',
            align: 'left', renderCell: ({ row }) => row?.orderStatus
                ? <Chip size="small" label={row.orderStatus} sx={{ ...getStatusChipSx(row.orderStatus), fontSize: 12 }} />
                : <Typography sx={{ fontSize: SIZE.xs, color: SUBTLE }}>—</Typography>,
        },
        {
            field: 'timeline', headerAlign: "left", flex: 0.9, minWidth: 260, phoneMinWidth: 170, headerName: 'Timeline',
            align: 'left', renderCell: ({ row }) => {
                // Below tablet width the two date groups stack, so the four dates never overlap.
                const kSx = { fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' };
                const vSx = (color) => ({ fontFamily: FONT, fontSize: '0.75rem', fontWeight: WEIGHT.semibold, color: color || NAVY, mt: 0.15, whiteSpace: 'nowrap' });
                return (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: 1, py: 0.5, width: '100%' }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={kSx}>Created</Typography>
                            <Typography sx={vSx()}>{row?.createdAt ? parseDateAndTime(row.createdAt, true) : '-'}</Typography>
                            <Typography sx={{ ...kSx, mt: 0.4 }}>Updated</Typography>
                            <Typography sx={vSx()}>{row?.updatedAt ? parseDateAndTime(row.updatedAt, true) : '-'}</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER, display: { xs: 'none', md: 'block' } }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={kSx}>Resolution Due</Typography>
                            {/* Waiting on the brand pauses the SLA; the due date moves on once they reply. */}
                            <Typography sx={vSx(row?.slaPausedAt ? ACCENTS.amber.solid : ERROR)}>
                                {row?.slaPausedAt ? 'Paused — your reply' : row?.resolutionDueAt ? parseDateAndTime(row.resolutionDueAt, true) : '-'}
                            </Typography>
                            <Typography sx={{ ...kSx, mt: 0.4 }}>Created By</Typography>
                            <Typography sx={{ ...vSx(), whiteSpace: 'normal' }}>{row?.createdByName || '-'}</Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'status', headerAlign: "left", flex: 0.5, minWidth: 150, headerName: 'Ticket Status',
            align: 'left', renderCell: ({ row }) => <StatusChip row={row} />,
        },
        {
            field: 'action', headerAlign: "center", flex: 0.3, minWidth: 110, headerName: 'Actions',
            align: 'center', sortable: false, renderCell: ({ row }) => {
                const closed = row?.statusCategory === 'CLOSED';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="View ticket">
                            <IconButton size="small" aria-label="View ticket" sx={iconBtnSx(PRIMARY_BLUE)} onClick={() => navigate(`/support-v2/view?ticketId=${row.id}`)}>
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        {canReopen(row) && (
                            <Tooltip title="Reopen ticket">
                                <IconButton size="small" aria-label="Reopen ticket" sx={iconBtnSx(ACCENTS.navy.solid)} onClick={() => setReopenConfirmRow(row)}>
                                    <ReplayIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={closed ? "Already closed" : "Close ticket"}>
                            <span>
                                <IconButton size="small" disabled={closed} aria-label={closed ? "Already closed" : "Close ticket"} sx={iconBtnSx(ERROR)} onClick={() => setCloseConfirmRow(row)}>
                                    <CancelIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <Box sx={pageContainerSx}>
            <PageTitle>Help & Support</PageTitle>

            {/* Hero: what this page is, and the three things you do here. */}
            <Box sx={{
                ...rise(0), position: 'relative', overflow: 'hidden', borderRadius: '16px', mb: 2.5, px: { xs: 2, sm: 3 }, py: { xs: 2.25, sm: 2.75 },
                color: WHITE, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                background: `radial-gradient(circle at 92% 0%, rgba(255,255,255,.18) 0, rgba(255,255,255,0) 34%),
                    radial-gradient(circle at 70% 130%, rgba(91,162,240,.45) 0, rgba(91,162,240,0) 42%),
                    linear-gradient(118deg, #14263F 0%, #1E3A5F 45%, ${PRIMARY_BLUE} 100%)`,
                boxShadow: `0 10px 30px ${withOpacity(PRIMARY_BLUE, 0.25)}`,
            }}>
                <Box sx={{ width: 52, height: 52, borderRadius: '14px', flexShrink: 0, bgcolor: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.22)',
                    display: 'grid', placeItems: 'center', backdropFilter: 'blur(4px)' }}>
                    <SupportAgentOutlinedIcon sx={{ color: WHITE, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: { xs: SIZE.lg, sm: SIZE['2xl'] }, fontWeight: WEIGHT.extra, lineHeight: 1.2 }}>Help & Support</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, opacity: 0.85, mt: 0.4 }}>
                        Raise, track and resolve support tickets for your shipments. Prozo replies on the ticket; you&apos;re emailed at each update.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={() => setCreateOpen(true)} startIcon={<AddCircleOutlineIcon />}
                        sx={{ ...heroBtnSx, bgcolor: WHITE, color: '#1E3A5F', '&:hover': { bgcolor: '#EAF2FC', boxShadow: 'none' } }}>
                        Create Ticket
                    </Button>
                    <Button variant="outlined" onClick={() => setBulkOpen(true)} startIcon={<UploadFileIcon />}
                        sx={{ ...heroBtnSx, color: WHITE, borderColor: 'rgba(255,255,255,.5)', '&:hover': { borderColor: WHITE, bgcolor: 'rgba(255,255,255,.08)' } }}>
                        Bulk Create
                    </Button>
                    <Button variant="outlined" onClick={() => setExportTarget({ filters: listFilters, count: totalTickets })} startIcon={<GetAppIcon />}
                        sx={{ ...heroBtnSx, color: WHITE, borderColor: 'rgba(255,255,255,.5)', '&:hover': { borderColor: WHITE, bgcolor: 'rgba(255,255,255,.08)' } }}>
                        Export
                    </Button>
                </Box>
            </Box>

            {/* KPI tiles — each one filters the list. */}
            <Box component="section" aria-label="Ticket summary" sx={{
                display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(7, minmax(0, 1fr))' },
                gap: { xs: 1.25, md: 1.75 }, mb: 2,
            }}>
                {kpis.map((k, i) => (
                    <KpiTile key={k.label} label={k.label} value={k.value} sub={k.sub} info={k.info} icon={k.icon} accent={k.accent}
                        loading={statsLoading && !summary} order={i + 1} active={k.key ? drill === k.key : false}
                        onClick={() => toggleDrill(k.key)} />
                ))}
            </Box>

            {/* Waiting on the brand: the one thing on this page that needs them. */}
            {awaitingCount > 0 && drill !== 'awaiting' && !showArchived && (
                <Box sx={{ ...rise(7), display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2, px: 2, py: 1.25, borderRadius: '12px',
                    bgcolor: ACCENTS.amber.soft, border: `1px solid ${withOpacity(ACCENTS.amber.solid, 0.25)}` }}>
                    <MarkEmailUnreadOutlinedIcon sx={{ color: ACCENTS.amber.solid }} />
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: '#7C3A06', flex: '1 1 240px' }}>
                        <strong>{awaitingCount} {awaitingCount === 1 ? 'ticket is' : 'tickets are'} waiting for your reply.</strong>{' '}
                        Without a reply in 72 hours, a ticket is closed.
                    </Typography>
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => toggleDrill('awaiting')}
                        sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.bold, color: ACCENTS.amber.solid }}>
                        Show them
                    </Button>
                </Box>
            )}

            {/* Filters */}
            <Box sx={{ ...CARD_SX, ...rise(8), mb: 2, overflow: 'hidden' }}>
                <Box component="button" type="button" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen}
                    sx={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: 1.25, px: 2.25, py: 1.5,
                        '&:hover': { bgcolor: '#F8FAFD' }, '&:focus-visible': { outline: `2px solid ${PRIMARY_BLUE}`, outlineOffset: -2 } }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: withOpacity(PRIMARY_BLUE, 0.1), display: 'grid', placeItems: 'center' }}>
                        <TuneOutlinedIcon sx={{ fontSize: 17, color: PRIMARY_BLUE }} />
                    </Box>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY, flex: 1 }}>
                        Filters
                        {activeChips.length > 0 && (
                            <Box component="span" sx={{ ml: 1, px: 0.9, py: 0.1, borderRadius: '999px', bgcolor: PRIMARY_BLUE, color: WHITE, fontSize: 12 }}>
                                {activeChips.length}
                            </Box>
                        )}
                    </Typography>
                    <ExpandMoreIcon sx={{ color: SUBTLE, transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
                </Box>
                <Collapse in={filtersOpen}>
                    <Box sx={{ px: 2.25, pb: 2.25, pt: 0.5, borderTop: `1px solid ${BORDER}` }}>
                        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                            <Box data-rp-filters sx={{ display: 'grid', gap: 1.75, mt: 1.5, alignItems: 'end',
                                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                                <Box sx={{ minWidth: 0, '& .MuiInputLabel-root': { display: 'none' }, '& legend': { display: 'none' } }}>
                                    <FieldLabel>Date Range</FieldLabel>
                                    <DateRangePicker defaultRange={dateRange} maxDate={new Date()} showTextBox onChange={(range) => setDateRange(range)} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <FieldLabel>Ticket ID</FieldLabel>
                                    <RHFTextField label="" name="ticketId" placeholder="ESC or Freshdesk no." size="small" fullWidth />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <FieldLabel>AWB</FieldLabel>
                                    <RHFTextField label="" name="awb" placeholder="AWB number" size="small" fullWidth />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <FieldLabel>Status</FieldLabel>
                                    <RHFMultipleSelectField label="" name="status" variant="outlined" width="100%" compact selectAll options={statusOptions} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <FieldLabel>Category</FieldLabel>
                                    <RHFMultipleSelectField label="" name="category" variant="outlined" width="100%" compact search selectAll options={categoryOptions} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <FieldLabel>Subcategory</FieldLabel>
                                    <RHFMultipleSelectField label="" name="subcategory" variant="outlined" width="100%" compact search selectAll options={subcategoryOptions} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <FieldLabel>Courier</FieldLabel>
                                    <RHFMultipleSelectField label="" name="courier" variant="outlined" width="100%" compact search selectAll options={courierOptions} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <FieldLabel>Shipment Status</FieldLabel>
                                    <RHFMultipleSelectField label="" name="shipmentStatus" variant="outlined" width="100%" compact search selectAll options={shipmentStatusOptions} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                    <Button type="submit" variant="contained" sx={{ ...filterBtnSx, boxShadow: 'none' }}>Apply</Button>
                                    <Button variant="outlined" onClick={onReset}
                                        sx={{ fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.bold, textTransform: 'uppercase', letterSpacing: '0.5px', borderRadius: '8px', color: SUBTLE, borderColor: BORDER }}>
                                        Reset
                                    </Button>
                                </Box>
                            </Box>
                        </FormProvider>
                    </Box>
                </Collapse>
            </Box>

            {/* Ticket list */}
            <Box sx={{ ...CARD_SX, ...rise(9), overflow: 'hidden' }}>
                <Box sx={{ px: 2.25, py: 1.75, borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
                            {showArchived ? 'Archived Tickets' : 'Your Tickets'}
                            <Box component="span" sx={{ ml: 1, color: SUBTLE, fontWeight: WEIGHT.semibold, fontVariantNumeric: 'tabular-nums' }}>
                                {totalTickets.toLocaleString('en-IN')}
                            </Box>
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: SUBTLE, mt: 0.25 }}>
                            {showArchived
                                ? 'Closed tickets from the previous support desk, for the selected date range'
                                : appliedFilters.escalationNumber
                                    ? `Tickets matching "${appliedFilters.escalationNumber}", from any date`
                                    : 'Tick tickets to download them, or click a tile above to narrow the list'}
                        </Typography>
                    </Box>
                    {/* Current / Archived */}
                    <Box role="radiogroup" aria-label="Which tickets" sx={{ display: 'inline-flex', p: 0.4, gap: 0.25, borderRadius: '999px', bgcolor: '#F1F4F9', border: `1px solid ${BORDER}` }}>
                        {[{ v: false, label: 'Current', icon: ConfirmationNumberOutlinedIcon }, { v: true, label: 'Archived', icon: Inventory2OutlinedIcon }].map((o) => {
                            const on = showArchived === o.v;
                            const Icon = o.icon;
                            return (
                                <Box key={o.label} component="button" type="button" role="radio" aria-checked={on}
                                    onClick={() => { if (!on) { setPage(0); setShowArchived(o.v); } }}
                                    sx={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.5, py: 0.5,
                                        borderRadius: '999px', fontFamily: FONT, fontSize: 13, fontWeight: WEIGHT.bold, color: on ? WHITE : SUBTLE,
                                        background: on ? ACCENTS.blue.grad : 'transparent', boxShadow: on ? `0 3px 8px ${withOpacity(PRIMARY_BLUE, 0.3)}` : 'none',
                                        transition: 'background-color 150ms, color 150ms', '&:hover': on ? {} : { color: PRIMARY_BLUE, bgcolor: WHITE },
                                        '&:focus-visible': { outline: `2px solid ${PRIMARY_BLUE}`, outlineOffset: 1 } }}>
                                    <Icon sx={{ fontSize: 16 }} />{o.label}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {activeChips.length > 0 && (
                    <Box sx={{ px: 2.25, py: 1.25, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${BORDER}`, bgcolor: '#FAFBFD' }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: SUBTLE, fontWeight: WEIGHT.semibold, mr: 0.5 }}>Showing only:</Typography>
                        {activeChips.map((c) => (
                            <Chip key={c.key} size="small" label={c.label} onDelete={c.onDelete}
                                sx={{ fontFamily: FONT, fontSize: 12, fontWeight: WEIGHT.semibold, maxWidth: 360,
                                    bgcolor: c.accent ? ACCENTS.amber.soft : withOpacity(PRIMARY_BLUE, 0.08), color: c.accent ? ACCENTS.amber.solid : PRIMARY_BLUE,
                                    '& .MuiChip-deleteIcon': { color: 'inherit', opacity: 0.7 } }} />
                        ))}
                        <Button size="small" onClick={onReset} sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.bold, fontSize: 12, color: SUBTLE }}>Clear all</Button>
                    </Box>
                )}

                {/* Selection bar */}
                <Collapse in={selectionCount > 0}>
                    <Box sx={{ px: 2.25, py: 1.1, display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', bgcolor: withOpacity(PRIMARY_BLUE, 0.06),
                        borderBottom: `1px solid ${withOpacity(PRIMARY_BLUE, 0.18)}` }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>
                            {allMatching ? `All ${totalTickets.toLocaleString('en-IN')} matching tickets selected` : `${selectedIds.length} selected`}
                        </Typography>
                        {!allMatching && wholePageTicked && totalTickets > selectedIds.length && (
                            <Button size="small" onClick={() => setAllMatching(true)}
                                sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.bold, fontSize: 12.5, color: PRIMARY_BLUE }}>
                                Select all {totalTickets.toLocaleString('en-IN')} matching tickets
                            </Button>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" variant="contained" startIcon={<FileDownloadOutlinedIcon />} onClick={downloadSelected}
                            sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.bold, borderRadius: '8px', boxShadow: 'none', background: ACCENTS.blue.grad }}>
                            Download selected
                        </Button>
                        <Button size="small" onClick={clearSelection} sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.bold, color: SUBTLE }}>
                            Clear selection
                        </Button>
                    </Box>
                </Collapse>

                <Box sx={{ p: 1, '& .MuiDataGrid-row': { transition: 'background-color 120ms' }, '& .MuiDataGrid-row:hover': { bgcolor: '#F6F9FE' } }}>
                    <ProzoDataGrid
                        columns={columns}
                        rows={resData?.results || []}
                        loading={isLoading}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        setPage={setPage}
                        page={page}
                        pagination
                        hideFooterPagination={false}
                        paginationMode="server"
                        rowCount={totalTickets}
                        getRowId={(row) => row.id}
                        checkboxSelection
                        keepNonExistentRowsSelected
                        rowSelectionModel={selectedIds}
                        setRowSelectionModel={(ids) => { setSelectedIds(ids); if (allMatching) setAllMatching(false); }}
                        boxShadow={0}
                    />
                </Box>
            </Box>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Create Ticket
                    <IconButton onClick={() => setCreateOpen(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <CreateEscalationTicket
                        onClose={() => setCreateOpen(false)}
                        onCreated={() => { setPage(0); setReloadTick((t) => t + 1); }}
                    />
                </DialogContent>
            </Dialog>

            <BulkCreateTickets open={bulkOpen} onClose={() => setBulkOpen(false)}
                onDone={() => { setPage(0); setReloadTick((t) => t + 1); }} />

            <ExportTicketsDialog open={!!exportTarget} onClose={() => setExportTarget(null)}
                filters={exportTarget?.filters} count={exportTarget?.count} selection={!!exportTarget?.selection} />

            <Dialog open={!!reopenConfirmRow} onClose={() => !reopeningTicket && (setReopenConfirmRow(null), setReopenComment(""))} maxWidth="xs" fullWidth>
                <DialogTitle>Reopen ticket {reopenConfirmRow?.escalationNumber}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13.5, color: SUBTLE, mb: 1.5 }}>
                        The ticket goes back to Prozo&apos;s team. Tell them what still needs fixing.
                    </Typography>
                    <TextField autoFocus multiline minRows={3} fullWidth required placeholder="The parcel still hasn't been delivered"
                        value={reopenComment} onChange={(e) => setReopenComment(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setReopenConfirmRow(null); setReopenComment(""); }} disabled={reopeningTicket} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <LoadingButton variant="contained" loading={reopeningTicket} onClick={submitReopenTicket} disabled={!reopenComment.trim()} sx={{ textTransform: 'none' }}>
                        Reopen Ticket
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog open={!!closeConfirmRow} onClose={() => !closingTicket && setCloseConfirmRow(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Close ticket {closeConfirmRow?.escalationNumber}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13.5, color: SUBTLE }}>
                        This marks the ticket as closed. A closed ticket can&apos;t be reopened, so raise a new one if the issue comes back.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCloseConfirmRow(null)} disabled={closingTicket} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <LoadingButton variant="contained" color="error" loading={closingTicket} onClick={submitCloseTicket} sx={{ textTransform: 'none' }}>
                        Close Ticket
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default withLayout(EscalationManagementV2Merchant);
